import { FC, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext, launchCrystalProvisioning, countOwnerPortalMemberships } from "context/AuthContext";
import { IInstanceWithRole } from "context/InstanceContext";
import { fetchInstanceList } from "../../services/instance";
import { Ring } from "@uiball/loaders";
import { seatStatusAsync, ISeatStatus } from "../../services/crystal";
import styles from "./index.module.css";

type Phase = "polling" | "done" | "canceled" | "timeout" | "no_grant";

const CrystalSuccess: FC = () => {
  const navigate = useNavigate();
  const authInfo = useContext(AuthContext);

  const [searchParams] = useSearchParams();
  // Read the grant id from the query string (added by the server on success_url).
  const seatGrantId = searchParams.get("seat_grant_id");

  const [phase, setPhase] = useState<Phase>(seatGrantId ? "polling" : "no_grant");
  const [seat, setSeat] = useState<ISeatStatus | null>(null);
  // Fetched once the seat goes active, not on click: instancesWithRole
  // (InstanceContext) is an app-boot snapshot from before this instance
  // existed, so it can't stand in for a real membership row.
  const [membershipList, setMembershipList] = useState<IInstanceWithRole[]>([]);
  const [membershipLoading, setMembershipLoading] = useState(false);

  useEffect(() => {
    if (!seatGrantId) return;

    let cancelled = false;
    // Poll about once a second. A seat that stays "preparing" is paid for and
    // safe, so a long wait is a soft "still preparing", never a hard error.
    const MAX_TRIES = 90;
    const INTERVAL_MS = 1000;

    (async () => {
      for (let i = 0; i < MAX_TRIES && !cancelled; i++) {
        try {
          const s = await seatStatusAsync(seatGrantId);
          if (cancelled) return;
          setSeat(s);
          const quantity = s.quantity ?? 1;
          if (
            s.status === "active" &&
            s.msp_id &&
            s.subject_ids.length >= quantity
          ) {
            setPhase("done");
            setMembershipLoading(true);
            try {
              const list = await fetchInstanceList();
              if (!cancelled) setMembershipList(list);
            } catch (err) {
              console.error(err);
            } finally {
              if (!cancelled) setMembershipLoading(false);
            }
            return;
          }
          if (s.status === "canceled") {
            setPhase("canceled");
            return;
          }
          // null / past_due / unpaid / not-yet-placed / not-yet-bound: keep polling
        } catch (err) {
          console.error(err);
        }
        await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
      }
      if (!cancelled) setPhase("timeout");
    })();

    return () => {
      cancelled = true;
    };
  }, [seatGrantId]);

  // Where "Start Provisioning" sends the customer: into that instance's
  // provisioning page (Part 2 lives inside the MSP instance). The route is keyed
  // by the box id — subject_ids are the crystal boxes bound to this seat.
  const boxId = seat?.subject_ids?.[0];
  const canProvision = Boolean(seat?.instance && boxId);

  if (phase === "polling" || phase === "timeout" || phase === "no_grant") {
    return (
      <div className={styles["container"]}>
        <div className={styles["content"]}>
          <Ring size={60} />
          <h1 className={styles["headline"]}>Preparing your crystal</h1>
        </div>
      </div>
    );
  }

  if (phase === "canceled") {
    return (
      <div className={styles["container"]}>
        <div className={`${styles["content"]} ${styles["canceled"]}`}>
          <h1 className={styles["headline"]}>Payment unsuccessful</h1>
          <p className={styles["subtitle"]}>
            Your transaction was canceled. You can purchase a new Crystal Seat.
          </p>
          <button
            type="button"
            className={styles["start-btn"]}
            onClick={() => navigate("/crystal")}
          >
            Buy a New Crystal
          </button>
          <button
            type="button"
            className={styles["home-link"]}
            onClick={() => navigate("/")}
          >
            Back to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["container"]}>
      <div className={styles["content"]}>
        <h1 className={styles["headline"]}>Firewalla Crystal is Ready to Go</h1>
        <p className={styles["subtitle"]}>
          Deploy Firewalla Crystal on any hardware. Simply follow the
          installation guide below to set up your device and secure your network.
        </p>
        {canProvision && (
          <button
            className={styles["start-btn"]}
            data-gtm-id="start_provisioning"
            disabled={membershipLoading}
            onClick={() => {
              // use_portal decides same-origin (dev/beta/prod all resolve
              // correctly) vs the raw per-instance domain. membershipList was
              // fetched as soon as the seat went active (see above); a miss
              // (e.g. that fetch failed) falls back to seat's bare id/domain.
              const membership = membershipList.find((m) => m.id === seat!.msp_id);
              launchCrystalProvisioning(
                membership ?? { id: seat!.msp_id!, domain: seat!.instance!, use_portal: seat!.use_portal },
                countOwnerPortalMemberships(membershipList),
                boxId!
              );
            }}
          >
            {membershipLoading ? <Ring size={18} /> : "Start Provisioning"}
          </button>
        )}
        <div className={styles["illustration"]}>
          <img src="/images/checkout-success.svg" alt="Firewalla Crystal" />
        </div>
        <p className={styles["more-info"]}>
          We'll send an email to{" "}
          <span className={styles["email"]}>
            {authInfo.email || "your email"}
          </span>{" "}
          with a link to your account once everything is set up. In the
          meantime, feel free to check out{" "}
          <a
            href="https://help.firewalla.com/hc/en-us/articles/4409866753427"
            target="_blank"
            rel="noreferrer"
            data-gtm-id="view_article"
          >
            our article
          </a>{" "}
          on Firewalla MSP's key features and capabilities.
        </p>
      </div>
    </div>
  );
};

export default CrystalSuccess;
